import { useEffect, useRef, useState } from "react";
import { api, extractError, API_BASE } from "@/lib/apiClient";
import { toast } from "sonner";
import { Sparkles, Save, Loader2, X, RefreshCcw, FileText, Mic, Play, Pause, Volume2, Globe } from "lucide-react";

const LANG_LABELS = {
  en: "English",
  hi: "हिन्दी (Hindi)",
  ta: "தமிழ் (Tamil)",
  te: "తెలుగు (Telugu)",
  kn: "ಕನ್ನಡ (Kannada)",
  mr: "मराठी (Marathi)",
  bn: "বাংলা (Bengali)",
};

/**
 * Modal that lets a specialist review the AI-drafted SOAP note. In the
 * new backend flow the note is generated automatically the moment a
 * recording uploads — so this modal typically polls `queued/generating`
 * until it flips to `draft`, then lets the doctor edit and save.
 */
export default function AiNoteModal({ bookingId, onClose, onSaved }) {
  const [status, setStatus] = useState("loading"); // loading|not_generated|queued|generating|draft|saved|failed|saving
  const [note, setNote] = useState("");
  const [transcript, setTranscript] = useState("");
  const [error, setError] = useState("");
  const [savedAt, setSavedAt] = useState(null);
  const [genAt, setGenAt] = useState(null);
  const [library, setLibrary] = useState([]);
  const [clipId, setClipId] = useState("warm_closing");
  const [playing, setPlaying] = useState(null); // clipId currently playing
  const [patientLang, setPatientLang] = useState("en");
  const previewRef = useRef(null);
  const pollRef = useRef(null);

  const clearPoll = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const applyPayload = (data) => {
    setNote(data.note || "");
    setTranscript(data.transcript || "");
    setSavedAt(data.saved_at);
    setGenAt(data.generated_at);
    setError(data.error || "");
    setStatus(data.status || "not_generated");
    if (data.patient_language) setPatientLang(data.patient_language);
  };

  const load = async () => {
    try {
      const { data } = await api.get(`/bookings/${bookingId}/session-note`);
      applyPayload(data);
    } catch (e) {
      toast.error(extractError(e, "Could not load note"));
      setStatus("failed");
    }
  };

  useEffect(() => {
    load();
    return () => {
      clearPoll();
      if (previewRef.current) {
        previewRef.current.pause();
        previewRef.current = null;
      }
    };
    // eslint-disable-next-line
  }, [bookingId]);

  // Refetch the clip library whenever patient language is known/changes so
  // the transcripts + audio match what the patient will actually receive.
  useEffect(() => {
    api.get(`/voice/manaswini/library?lang=${patientLang}`)
      .then((r) => setLibrary(r.data.clips || []))
      .catch(() => {});
  }, [patientLang]);

  // Poll every 4s while note is queued/generating
  useEffect(() => {
    if (status === "queued" || status === "generating") {
      pollRef.current = setInterval(load, 4000);
      return clearPoll;
    }
    clearPoll();
    // eslint-disable-next-line
  }, [status]);

  const regenerate = async () => {
    setStatus("generating");
    try {
      const { data } = await api.post(`/bookings/${bookingId}/session-note/generate`);
      setNote(data.note); setTranscript(data.transcript); setGenAt(new Date().toISOString());
      setStatus("draft");
      toast.success("AI note draft is ready");
    } catch (e) {
      toast.error(extractError(e));
      setStatus("failed");
      setError(extractError(e));
    }
  };

  const save = async () => {
    if (!note || note.trim().length < 10) return toast.error("Note is too short");
    setStatus("saving");
    try {
      await api.post(`/bookings/${bookingId}/session-note`, { note, manaswini_clip_id: clipId });
      setSavedAt(new Date().toISOString());
      setStatus("saved");
      toast.success("Saved to the patient's file");
      if (onSaved) onSaved();
    } catch (e) {
      toast.error(extractError(e));
      setStatus("draft");
    }
  };

  const togglePreview = (id) => {
    // Stop any current playback
    if (previewRef.current) {
      previewRef.current.pause();
      previewRef.current = null;
    }
    if (playing === id) {
      setPlaying(null);
      return;
    }
    const a = new Audio(`${API_BASE}/voice/manaswini/${id}.mp3?lang=${patientLang}`);
    a.onended = () => setPlaying(null);
    a.onerror = () => {
      setPlaying(null);
      toast.error("Couldn't play that clip");
    };
    previewRef.current = a;
    setPlaying(id);
    a.play().catch(() => {
      setPlaying(null);
      toast.error("Playback blocked by the browser — try again after another click");
    });
  };

  const busy = status === "loading" || status === "queued" || status === "generating" || status === "saving";

  return (
    <div
      data-testid="ai-note-modal"
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
      onClick={() => !busy && onClose()}
    >
      <div
        className="bg-white rounded-3xl w-full max-w-2xl p-6 lg:p-7 max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between">
          <div>
            <span className="uppercase-label inline-flex items-center gap-1">
              <Sparkles size={12} /> AI session note
            </span>
            <h3 className="font-display text-2xl mt-1">Clinician draft</h3>
            <div className="text-xs text-[color:var(--mano-ink-soft)] mt-1">
              {savedAt ? `Saved · ${new Date(savedAt).toLocaleString()}`
                : genAt ? `Draft generated · ${new Date(genAt).toLocaleString()}`
                : "Awaiting draft"}
            </div>
          </div>
          <button data-testid="ai-note-close" onClick={onClose} className="p-1" disabled={busy}><X size={18} /></button>
        </div>

        {status === "loading" && (
          <div className="mt-6 text-[color:var(--mano-ink-soft)] flex items-center gap-2">
            <Loader2 size={14} className="animate-spin" /> Loading…
          </div>
        )}

        {(status === "queued" || status === "generating") && (
          <div className="mt-6 flex items-center justify-center rounded-2xl border border-[color:var(--mano-border)] p-8">
            <div className="text-center">
              <Loader2 size={22} className="animate-spin mx-auto text-[color:var(--mano-primary)]" />
              <div className="font-display text-lg mt-2">
                {status === "queued" ? "Note is queued…" : "Transcribing and drafting your note…"}
              </div>
              <div className="text-sm text-[color:var(--mano-ink-soft)] mt-1">
                This started automatically when the recording finished uploading. It usually takes 15-40 seconds.
              </div>
            </div>
          </div>
        )}

        {(status === "not_generated" || status === "failed") && (
          <div className="mt-6">
            <div className="rounded-2xl border border-dashed border-[color:var(--mano-border)] p-6 text-center">
              <FileText size={22} className="mx-auto text-[color:var(--mano-primary)]" />
              <div className="font-display text-lg mt-2">
                {status === "failed" ? "Note generation failed" : "No note yet"}
              </div>
              {error && <div className="text-sm text-[color:var(--mano-high)] mt-2">{error}</div>}
              <button
                data-testid="ai-note-generate"
                onClick={regenerate}
                className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[color:var(--mano-primary)] hover:bg-[color:var(--mano-primary-hover)] text-white font-semibold transition-colors"
              >
                <Sparkles size={14} /> {status === "failed" ? "Retry generation" : "Generate draft note"}
              </button>
            </div>
          </div>
        )}

        {(status === "draft" || status === "saving" || status === "saved") && (
          <>
            <div className="mt-5">
              <label className="uppercase-label inline-flex items-center gap-1">
                <FileText size={12} /> SOAP note (editable)
              </label>
              <textarea
                data-testid="ai-note-textarea"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                disabled={status === "saving" || status === "saved"}
                className="mt-2 w-full h-64 p-4 rounded-2xl border border-[color:var(--mano-border)] bg-[color:var(--mano-bg)] font-mono text-sm leading-relaxed focus:outline-none focus:border-[color:var(--mano-primary)] disabled:opacity-70"
              />
            </div>

            {transcript && (
              <details className="mt-4">
                <summary className="uppercase-label cursor-pointer">View full transcript</summary>
                <pre className="mt-2 whitespace-pre-wrap text-sm text-[color:var(--mano-ink-soft)] p-4 rounded-2xl border border-[color:var(--mano-border)] bg-[color:var(--mano-bg)] max-h-56 overflow-y-auto">
{transcript}
                </pre>
              </details>
            )}

            {library.length > 0 && status !== "saved" && (
              <div className="mt-5" data-testid="ai-note-manaswini-picker">
                <label className="uppercase-label inline-flex items-center gap-1">
                  <Mic size={12} /> Studio voice for this recap
                </label>
                <div className="mt-2 space-y-2">
                  {library.map((c) => {
                    const selected = clipId === c.id;
                    const isPlaying = playing === c.id;
                    return (
                      <div
                        key={c.id}
                        data-testid={`manaswini-clip-${c.id}`}
                        onClick={() => setClipId(c.id)}
                        className={`rounded-2xl border p-3 pl-4 cursor-pointer transition-colors flex items-center gap-3 ${
                          selected
                            ? "border-[color:var(--mano-primary)] bg-[color:var(--mano-primary)]/5"
                            : "border-[color:var(--mano-border)] bg-white hover:border-[color:var(--mano-primary)]/40"
                        }`}
                      >
                        <div
                          className={`h-4 w-4 rounded-full flex items-center justify-center border-2 flex-shrink-0 ${
                            selected
                              ? "border-[color:var(--mano-primary)] bg-[color:var(--mano-primary)]"
                              : "border-[color:var(--mano-border)]"
                          }`}
                        >
                          {selected && <div className="h-1.5 w-1.5 rounded-full bg-white" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-sm">{c.label}</span>
                            <span
                              className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full ${
                                c.has_recording
                                  ? "bg-[color:var(--mano-normal)]/15 text-[color:var(--mano-normal)]"
                                  : "bg-[color:var(--mano-ink)]/10 text-[color:var(--mano-ink-soft)]"
                              }`}
                              title={
                                c.has_recording
                                  ? "Real voice recording"
                                  : "Generated with studio voice (Nova)"
                              }
                            >
                              {c.has_recording ? "Real voice" : "Studio voice"}
                            </span>
                          </div>
                          <div className="text-xs text-[color:var(--mano-ink-soft)] mt-0.5">{c.desc}</div>
                        </div>
                        <button
                          type="button"
                          data-testid={`manaswini-preview-${c.id}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            togglePreview(c.id);
                          }}
                          className="h-9 w-9 rounded-full border border-[color:var(--mano-border)] flex items-center justify-center text-[color:var(--mano-primary)] hover:bg-[color:var(--mano-primary)]/10"
                          title={isPlaying ? "Stop preview" : "Preview clip"}
                        >
                          {isPlaying ? <Pause size={14} /> : <Play size={14} />}
                        </button>
                      </div>
                    );
                  })}
                </div>
                {(() => {
                  const selectedClip = library.find((c) => c.id === clipId);
                  if (!selectedClip) return null;
                  return (
                    <div
                      data-testid="ai-note-recap-preview"
                      className="mt-3 rounded-2xl border border-[color:var(--mano-primary)]/30 bg-gradient-to-br from-[color:var(--mano-primary)]/5 to-[color:var(--mano-mild)]/40 p-4"
                    >
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div className="flex items-center gap-2 text-[color:var(--mano-primary)] text-[11px] uppercase tracking-[0.16em] font-bold">
                          <Volume2 size={13} /> Preview what your patient will hear
                        </div>
                        <span
                          data-testid="ai-note-recap-preview-lang"
                          className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-[color:var(--mano-primary)]/10 text-[color:var(--mano-primary)] border border-[color:var(--mano-primary)]/20"
                          title="Patient's preferred language"
                        >
                          <Globe size={10} /> {LANG_LABELS[patientLang] || patientLang}
                        </span>
                      </div>
                      {selectedClip.text && (
                        <blockquote
                          data-testid="ai-note-recap-preview-transcript"
                          className="mt-3 text-sm leading-relaxed text-[color:var(--mano-ink)] italic border-l-2 border-[color:var(--mano-primary)]/40 pl-3"
                        >
                          &ldquo;{selectedClip.text}&rdquo;
                        </blockquote>
                      )}
                      <audio
                        key={`${clipId}-${patientLang}`}
                        data-testid="ai-note-recap-preview-audio"
                        src={`${API_BASE}/voice/manaswini/${clipId}.mp3?lang=${patientLang}`}
                        controls
                        preload="metadata"
                        className="mt-3 w-full h-9"
                      />
                      <div className="mt-2 text-[11px] text-[color:var(--mano-ink-soft)]">
                        This is the exact audio that will ride along with the recap email — attached and linked.
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            <div className="mt-6 flex items-center justify-between gap-3 flex-wrap">
              <button
                data-testid="ai-note-regen"
                onClick={regenerate}
                disabled={status === "saving"}
                className="inline-flex items-center gap-1 px-4 py-2 rounded-full border border-[color:var(--mano-border)] text-sm font-semibold hover:bg-[color:var(--mano-border)]/40 disabled:opacity-60"
              >
                <RefreshCcw size={14} /> Regenerate draft
              </button>
              {status === "saved" ? (
                <span className="inline-flex items-center gap-1 text-sm font-semibold text-[color:var(--mano-normal)]">
                  <Save size={14} /> Saved to patient file
                </span>
              ) : (
                <button
                  data-testid="ai-note-save"
                  onClick={save}
                  disabled={status === "saving"}
                  className="inline-flex items-center gap-1 px-5 py-2.5 rounded-full bg-[color:var(--mano-primary)] hover:bg-[color:var(--mano-primary-hover)] text-white font-semibold transition-colors disabled:opacity-60"
                >
                  {status === "saving" ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                  {status === "saving" ? "Saving…" : "Save to patient file"}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
